import { apiClient } from './api';

export interface Client {
  id: string;
  firstName: string;
  lastName: string;
  phone?: string;
  dateOfBirth?: string;
  allergies?: string;
  preferences?: string;
  marketingConsent: boolean;
  smsConsent: boolean;
  createdAt?: string;
  /** Adres kontaktowy z kartoteki. Brak = klient go nie podal. */
  email?: string | null;
  /** Konto do logowania — istnieje tylko wtedy, gdy klient podal adres. */
  user?: { email: string } | null;
  _count?: { bookings: number };
  totalSpent?: number;
  lastVisit?: string | null;
}

export interface ClientDetail extends Client {
  bookings: {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    status: string;
    totalPrice: number;
    service: { name: string; price: number; duration: number };
    staff: { firstName: string; lastName: string };
  }[];
  sales: {
    id: string;
    createdAt: string;
    paymentMethod: string;
    status: string;
    totalAmount: number;
    notes?: string;
    staff: { firstName: string; lastName: string };
    items: {
      id: string;
      name: string;
      quantity: number;
      unitPrice: number;
      total: number;
      service?: { name: string } | null;
    }[];
  }[];
  stats: {
    totalVisits: number;
    totalSpentBookings: number;
    totalSales: number;
    totalSpentSales: number;
    totalSpent: number;
  };
}

export interface CreateClientInput {
  salonId: string;
  firstName: string;
  lastName: string;
  /** Opcjonalny — klient z ulicy moze nie podac adresu. */
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  allergies?: string;
  preferences?: string;
  marketingConsent?: boolean;
  smsConsent?: boolean;
}

/**
 * Adres do pokazania: kartoteka jest zrodlem prawdy, konto tylko awaryjnie
 * (dane sprzed rozdzielenia kartoteki od konta).
 */
export const clientEmail = (c: { email?: string | null; user?: { email: string } | null }) =>
  c.email || c.user?.email || '';

export interface ClientStats {
  totalClients: number;
  newClients: number;
  returningClients: number;
  avgSpent: number;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export const clientApi = {
  /**
   * Uzupelnienie danych kartoteki przez salon. Podanie adresu tam, gdzie go
   * nie bylo, zaklada przy okazji konto do logowania.
   */
  async updateBySalon(
    clientId: string,
    salonId: string,
    data: { firstName?: string; lastName?: string; email?: string; phone?: string },
  ) {
    const res = await apiClient.patch<Client>(`/clients/${clientId}/salon/${salonId}`, data);
    return res.data;
  },

  async listBySalon(salonId: string, page = 1, limit = 20, search?: string) {
    const res = await apiClient.get<PaginatedResponse<Client>>(`/clients/salon/${salonId}`, {
      params: { page, limit, ...(search ? { search } : {}) },
    });
    return res.data;
  },

  async getDetail(clientId: string, salonId: string) {
    const res = await apiClient.get<ClientDetail>(`/clients/${clientId}/salon/${salonId}`);
    return res.data;
  },

  async getHistory(clientId: string, salonId: string) {
    const res = await apiClient.get(`/clients/${clientId}/salon/${salonId}/history`);
    return res.data;
  },

  async create(input: CreateClientInput) {
    const res = await apiClient.post<Client>('/clients', input);
    return res.data;
  },

  async getProfile() {
    const res = await apiClient.get('/clients/me');
    return res.data;
  },

  async updateProfile(data: Partial<Client>) {
    const res = await apiClient.put('/clients/me', data);
    return res.data;
  },

  async getStats(salonId: string) {
    const res = await apiClient.get<ClientStats>(`/clients/salon/${salonId}/stats`);
    return res.data;
  },
};
